// src/types/gates.ts
//
// TypeScript mirror of the gate document bogg writes at
// gates/{account_id}/markets/{condition_id} (contract: design/GATE.md).
// All timestamps are RFC3339 strings — bogg parses them with
// DateTime::parse_from_rfc3339, so never write Firestore timestamps here.

import { toIso } from './time';

export interface GateDecision {
    action: 'approve' | 'deny' | 'close' | null;
    /** the granted cap; null while action == 'approve' means UNBOUNDED */
    approved_limit_usd: number | null;
    acted_on_generation: number | null;
    decided_at: string | null;
    decided_by: string | null;
}

export interface GateDoc {
    account_id: string;
    condition_id: string;
    market_slug: string | null;
    market_end_date: string | null;
    /** vestigial — always 'pending', never branch on it */
    status: string;
    /** null today; ignored (irm authorizes via gate_operators) */
    owner_uid: string | null;
    proposed_limit_usd: number;
    /** serialized FairValueTarget — display-only context, drifts constantly */
    target?: Record<string, unknown>;
    request_generation: number;
    /** write-once, when the card was first created — GATE.md §4 */
    first_requested_at?: string | null;
    /** heartbeat — last time bogg confirmed the signal is still live — GATE.md §4 */
    last_seen_at?: string | null;
    updated_at: string;
    /** missing entirely reads as "no decision yet" — GATE.md §3 (all-nulls) */
    decision?: GateDecision;
}

/**
 * Read-boundary normalizer for gates/{account}/markets docs. The GATE.md
 * contract says all timestamps are RFC3339 strings, but bogg has shipped
 * native Firestore Timestamps in these fields (seen in prod 2026-07-20) —
 * which crash date consumers (`new Date(Timestamp)` is Invalid). Every
 * snapshot read must come through here so the rest of the UI can trust
 * the declared string types regardless of which form bogg wrote.
 */
export function normalizeGateDoc(raw: Record<string, unknown>): GateDoc {
    const doc = {
        ...raw,
        market_end_date: toIso(raw.market_end_date),
        first_requested_at: toIso(raw.first_requested_at),
        last_seen_at: toIso(raw.last_seen_at),
        updated_at: toIso(raw.updated_at) ?? '',
    } as GateDoc;
    if (raw.decision && typeof raw.decision === 'object') {
        const decision = raw.decision as Record<string, unknown>;
        doc.decision = {
            ...decision,
            decided_at: toIso(decision.decided_at),
        } as GateDecision;
    }
    return doc;
}

/**
 * Account-level control doc mode (design/GATE.md §3): whether an undecided
 * market holds ('gated') or auto-approves new exposure ('auto'). Explicit
 * approve/deny/close always binds regardless of mode.
 */
export type GateMode = 'auto' | 'gated';

/**
 * `gates/{account_id}` control document (design/GATE.md §3). `mode` is
 * user-owned once it exists; bogg only seeds it once when absent.
 */
export interface GateControlDoc {
    mode: GateMode;
    mode_changed_by: string | null;
    mode_changed_at: string | null;
    updated_at?: string | null;
}

/**
 * One entry from the polymarket-only `adminListAccounts` callable. Two id
 * forms on purpose: `id` is the registry-prefixed form the `accounts`
 * collection is keyed by (`polymarket:0x...`, matching the postgres
 * account registry); `address` is the bare form gate assignment and
 * display use everywhere else, since that's what bogg's gate docs are
 * keyed under (design/GATE.md §2).
 */
export interface GateAccountOption {
    id: string;
    address: string;
    name: string | null;
}

/**
 * A gate account bogg writes requests for that nobody registered yet
 * (#117) — surfaced by adminListAccounts' collection-group scan so admins
 * can register/assign it without retyping the id.
 */
export interface UnregisteredGateAccount {
    address: string;
    pending_requests: number;
}

/**
 * UI state of a gate row (design/GATE.md §5). Derived exclusively from the
 * generation handshake — status is vestigial.
 * - awaiting_decision:  no decision yet — show amount field + Allow/Discard.
 * - approved:           current-generation approve ($cap or unbounded).
 * - increase_requested: approved, but bogg bumped request_generation — the
 *                       algo wants materially more; old cap keeps applying
 *                       until the user acts (ignoring it is legitimate).
 * - denied:             current-generation deny; persists until changed.
 * - closing:            current-generation close; bogg sells to flat and
 *                       blocks re-entry until the decision changes.
 */
export type GateUiState =
    | 'awaiting_decision'
    | 'approved'
    | 'increase_requested'
    | 'denied'
    | 'closing';

export function deriveGateState(gate: GateDoc): GateUiState {
    const d = gate.decision;
    if (!d || d.action === null) return 'awaiting_decision';
    const answeredCurrent = d.acted_on_generation === gate.request_generation;
    if (d.action === 'approve')
        return answeredCurrent ? 'approved' : 'increase_requested';
    // deny/close never bump generation; a stale one shouldn't happen — treat
    // defensively as a fresh request (GATE.md §6 defensive note).
    if (!answeredCurrent) return 'awaiting_decision';
    return d.action === 'close' ? 'closing' : 'denied';
}

/**
 * The ONLY "needs action" signal: the user has not answered the current ask.
 * Drives badges, counts, and sort order. (There is no queryable server-side
 * equivalent — status is vestigial.)
 */
export function needsAttention(gate: GateDoc): boolean {
    return gate.decision?.acted_on_generation !== gate.request_generation;
}

/**
 * Staleness heartbeat threshold (design/GATE.md §11). bogg's heartbeat
 * interval is configurable — code default 45s, but ~2 minutes per cgb
 * (2026-07-17) — so the timeout must comfortably exceed one heartbeat plus
 * a generation tick. Tune alongside bogg's liveness_heartbeat_secs; a
 * timeout at or below the heartbeat interval makes healthy cards flap
 * into "signal paused". Raised 300s → 900s on 2026-07-20 after bogg's
 * heartbeat interval was increased to cut Firestore write volume.
 */
export const GATE_SIGNAL_TIMEOUT_MS = 900_000;

/**
 * Staleness overlay, orthogonal to deriveGateState: the market ended or the
 * heartbeat stopped. Stale cards render greyed, sort last, and are excluded
 * from attention counts — but stay decidable (a revive is harmless).
 */
export function isGateStale(gate: GateDoc, now: number = Date.now()): boolean {
    if (gate.market_end_date && new Date(gate.market_end_date).getTime() < now)
        return true;
    const heartbeat = gate.last_seen_at ?? gate.updated_at;
    return now - new Date(heartbeat).getTime() > GATE_SIGNAL_TIMEOUT_MS;
}

/**
 * Whether a card demands the operator's attention. In auto mode an undecided
 * card (action null) auto-approves and is informational, so it does not count;
 * an increase request always counts. Absent a mode, treat as gated.
 */
export function attentionRequired(gate: GateDoc, mode?: GateMode): boolean {
    if (!needsAttention(gate)) return false;
    if (mode === 'auto' && gate.decision?.action == null) return false;
    return true;
}
